import { Anchor, Box, Paper, Table } from "@mantine/core"
import { ClipboardTextIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { ListState } from "../../../components/ui"
import RenderText from "../../../components/ui/render-text"
import { IDashboard } from "../../../models/Dashboard"
import { dateFormat } from "../../../utils/date"
import { CardHeader } from "./CardHeader"
import { StatusPill } from "../../../components/ui/status-pill"
import { statusTone } from "../utils"
import classes from "../real.module.css"

interface Props {
  recentDeliveries: IDashboard["recentDeliveries"] | undefined
  isBuyer: boolean
  onViewAll: () => void
}

export function RecentOrdersCard({ recentDeliveries, isBuyer, onViewAll }: Props) {
  const { t } = useTranslation(["dashboard", "common", "supplierSales"])
  const orders = (recentDeliveries ?? []).slice(0, 5)

  return (
    <Paper className={`${classes.card} ${classes.cardPlain}`}>
      <Box className={classes.cardPad}>
        <CardHeader
          title={t("dashboard:recentDelivered")}
          helpKey="order.dashboard.section.recent-delivered"
          right={
            <Anchor onClick={onViewAll} className={classes.seeAll}>
              {t("dashboard:viewAll")}
            </Anchor>
          }
        />
      </Box>
      <Box className={classes.tableWrap}>
        <Table>
          <Table.Thead className={classes.tableHead}>
            <Table.Tr>
              <Table.Th>{t("dashboard:table.orderCode", { defaultValue: "Захиалга №" })}</Table.Th>
              <Table.Th>{t("dashboard:table.orderStatus")}</Table.Th>
              <Table.Th>{t(isBuyer ? "dashboard:table.supplier" : "supplierSales:customer.columns.partnerName")}</Table.Th>
              <Table.Th>{t("supplierSales:sales.columns.deliveryDate")}</Table.Th>
              <Table.Th ta="right">{t("dashboard:table.orderAmount")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          {orders.length > 0 && (
              <Table.Tbody>
                {orders.map((item, index) => {
                  const status =
                    item?.orderStatusName ??
                    (item as { status?: string })?.status ??
                    ""
                  // A buyer's own reference is the purchase code; the sales
                  // code belongs to the supplier's copy of the order.
                  const code = isBuyer
                    ? item?.purchaseCode || item?.salesCode
                    : item?.salesCode || item?.purchaseCode
                  // partnerBusiness is the portal-resolved counterparty. Fall
                  // back per portal so this still reads correctly against a
                  // backend that has not shipped that field yet.
                  const partner =
                    item?.partnerBusiness?.profileName ??
                    (isBuyer ? item?.senderBusiness?.profileName : item?.receiverBusiness?.profileName)
                  return (
                    <Table.Tr key={index}>
                      <Table.Td>
                        <RenderText text={`#${code || "-"}`} fontWeight={600} size="sm" />
                      </Table.Td>
                      <Table.Td>
                        <StatusPill tone={statusTone(status)}>{status || "-"}</StatusPill>
                      </Table.Td>
                      <Table.Td>
                        <RenderText text={partner} lineClamp={1} fontWeight={500} />
                      </Table.Td>
                      <Table.Td>
                        <RenderText text={dateFormat(item?.deliveredDate)} color="dimmed" />
                      </Table.Td>
                      <Table.Td ta="right">
                        <RenderText text={item?.orderAmount || 0} isAmount plainAmount fontWeight={600} />
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
          )}
        </Table>
        {orders.length === 0 && (
          <ListState
            state="empty"
            title={t("dashboard:recentEmpty.title")}
            description={t("dashboard:recentEmpty.text")}
            icon={<ClipboardTextIcon size={24} />}
          />
        )}
      </Box>
    </Paper>
  )
}
